export type ArtifactKind = 'html' | 'svg' | 'react' | 'code' | 'binary';

export type Artifact = {
  /** Stable id derived from the surrounding message — same text
   * inside the same message yields the same id across re-renders. */
  id: string;
  messageId: string;
  /** Raw language tag from the fenced block (lower-cased). */
  lang: string;
  kind: ArtifactKind;
  body: string;
  title?: string;
  /** For file-sourced artifacts: path relative to the user's
   * workspace root (e.g. `uploads/foo.pdf`). Enables download via
   * the /api/files/download endpoint. */
  relativePath?: string;
  /** File size in bytes (binary artifacts). */
  size?: number;
};

const LANG_TO_KIND: Record<string, ArtifactKind> = {
  html: 'html',
  htm: 'html',
  svg: 'svg',
  xml: 'svg',
  jsx: 'react',
  tsx: 'react',
  react: 'react',
};

/**
 * Decide whether a fenced code block is "artifact-worthy" — big
 * enough or structured enough that opening it in a side panel
 * beats reading it inline. HTML and SVG qualify at any length
 * (because preview is the point), React/JSX only past a meaningful
 * threshold so a 3-line snippet stays inline.
 */
export function isArtifactKind(lang: string | null | undefined, body: string): boolean {
  if (!body) return false;
  const l = (lang ?? '').toLowerCase();
  if (l === 'html' || l === 'htm' || l === 'svg' || l === 'xml') return true;
  if ((l === 'jsx' || l === 'tsx' || l === 'react') && body.length >= 200) return true;
  return false;
}

export function artifactKindFor(lang: string | null | undefined): ArtifactKind {
  const l = (lang ?? '').toLowerCase();
  return LANG_TO_KIND[l] ?? 'code';
}

/** Pull every artifact-worthy fenced block out of a message body. */
export function extractArtifactsFromText(text: string, messageId: string): Artifact[] {
  const out: Artifact[] = [];
  // Only match *closed* fenced blocks — otherwise a half-streamed
  // HTML tag pops into the iframe and sometimes crashes it.
  const re = /```([\w.+-]+)?\s*\n([\s\S]*?)```/g;
  let m: RegExpExecArray | null;
  let idx = 0;
  // biome-ignore lint/suspicious/noAssignInExpressions: canonical regex-loop
  while ((m = re.exec(text)) !== null) {
    const lang = m[1] ?? '';
    const body = m[2] ?? '';
    if (!isArtifactKind(lang, body)) {
      idx += 1;
      continue;
    }
    out.push({
      id: `${messageId}:${idx}`,
      messageId,
      lang: lang.toLowerCase(),
      kind: artifactKindFor(lang),
      body,
      title: firstTitleOf(lang, body),
    });
    idx += 1;
  }
  return out;
}

function firstTitleOf(lang: string, body: string): string | undefined {
  const l = lang.toLowerCase();
  if (l === 'html' || l === 'htm') {
    const t = /<title[^>]*>([^<]{1,80})<\/title>/i.exec(body);
    if (t?.[1]) return t[1].trim();
    const h1 = /<h1[^>]*>([^<]{1,80})<\/h1>/i.exec(body);
    if (h1?.[1]) return h1[1].trim();
  }
  // Fallback — first meaningful line, truncated.
  const firstLine = body.split('\n').find((l) => l.trim().length > 0) ?? '';
  return firstLine.slice(0, 60) || undefined;
}

const EXT_TO_KIND: Record<string, ArtifactKind> = {
  html: 'html',
  htm: 'html',
  svg: 'svg',
  xml: 'svg',
  jsx: 'react',
  tsx: 'react',
};

type ToolCallLike = {
  id: string;
  name: string;
  input: unknown;
};

/**
 * Surface artifacts that came from the agent *writing a file*
 * rather than inlining a fenced block in its reply. This is what
 * Claude Code usually does for HTML demos — it creates
 * `snake.html` in the workspace with a `Write` tool call and only
 * prints a one-line summary. Without this path, those files would
 * never show up in the Artifacts panel.
 */
export function extractArtifactsFromToolCalls(
  toolCalls: readonly ToolCallLike[] | undefined,
  messageId: string,
  username: string,
): Artifact[] {
  if (!toolCalls || toolCalls.length === 0) return [];
  const out: Artifact[] = [];
  for (const tc of toolCalls) {
    out.push(...extractArtifactsForToolCall(tc, messageId, username));
  }
  return out;
}

/**
 * Per-tool variant of `extractArtifactsFromToolCalls`. Used by the
 * inline renderer that places artifact cards directly under the
 * tool that produced them (instead of a separate section), so the
 * order of operations the user sees matches what the model
 * actually did.
 */
export function extractArtifactsForToolCall(
  tc: ToolCallLike,
  messageId: string,
  username: string,
): Artifact[] {
  if (tc.name !== 'Write' && tc.name !== 'Edit' && tc.name !== 'MultiEdit') return [];
  const input = (tc.input ?? {}) as Record<string, unknown>;
  const filePath = typeof input.file_path === 'string' ? input.file_path : '';
  if (!filePath) return [];
  const ext = filePath.split('.').pop()?.toLowerCase() ?? '';
  const kind = EXT_TO_KIND[ext];
  if (!kind) return [];
  const basename = filePath.split('/').pop() || filePath;
  const relativePath = workspaceRelativePath(filePath, username);
  if (kind === 'binary') {
    // Binary writes from the model are rare; the usual path is
    // Bash → python-docx/zipfile. Still, surface any binary Write
    // so the user gets a download card even in that edge case.
    return [
      {
        id: `${messageId}:tool:${tc.id}`,
        messageId,
        lang: ext,
        kind,
        body: '',
        title: basename,
        relativePath: relativePath ?? undefined,
      },
    ];
  }
  // Write gives us the full body on `content`; Edit gives
  // `new_string` which is only a patch, not a full file — skip
  // those for now, they'd preview wrong.
  const body = typeof input.content === 'string' ? input.content : '';
  if (!body) return [];
  return [
    {
      id: `${messageId}:tool:${tc.id}`,
      messageId,
      lang: ext,
      kind,
      body,
      title: basename,
      relativePath: relativePath ?? undefined,
    },
  ];
}

/**
 * Collect binary/docs files the agent *touched* via Bash tool use
 * (e.g. `python3 -c "..."` that imports python-docx and writes a
 * .docx). We can't see the body, and Bash input doesn't always
 * include the destination path, so we grep the command string for
 * paths under the user's workspace root and check their extensions.
 * Best-effort — misses dynamic filenames, catches obvious cases.
 */
export function extractBinaryArtifactsFromBash(
  toolCalls: readonly ToolCallLike[] | undefined,
  messageId: string,
  username: string,
): Artifact[] {
  if (!toolCalls || toolCalls.length === 0) return [];
  const out: Artifact[] = [];
  const seen = new Set<string>();
  for (const tc of toolCalls) {
    for (const a of extractBinaryArtifactsForToolCall(tc, messageId, username)) {
      // Dedupe across tool calls in the same message: two Bash
      // commands creating the same path shouldn't render twice.
      if (seen.has(a.relativePath ?? a.id)) continue;
      seen.add(a.relativePath ?? a.id);
      out.push(a);
    }
  }
  return out;
}

/** Per-tool variant for the inline renderer (artifact under tool). */
export function extractBinaryArtifactsForToolCall(
  tc: ToolCallLike,
  messageId: string,
  username: string,
): Artifact[] {
  if (tc.name !== 'Bash') return [];
  const input = (tc.input ?? {}) as Record<string, unknown>;
  const cmd = typeof input.command === 'string' ? input.command : '';
  if (!cmd) return [];
  const extensions = [
    'docx',
    'doc',
    'pdf',
    'pptx',
    'ppt',
    'xlsx',
    'xls',
    'zip',
    'txt',
    ...Object.keys(AUDIO_MIME_TYPES),
  ];
  const pathRe = new RegExp(
    `(?:/run/tenant-home/|/workspace/[^\\s/"'<>]+/)[^\\s"'<>]+\\.(${extensions.join('|')})(?=[\\s"'<>]|$)`,
    'gi',
  );
  const out: Artifact[] = [];
  const seen = new Set<string>();
  for (const m of cmd.matchAll(pathRe)) {
    const abs = m[0];
    if (seen.has(abs)) continue;
    seen.add(abs);
    const rel = workspaceRelativePath(abs, username);
    if (!rel) continue;
    const ext = m[1].toLowerCase();
    out.push({
      // The panel deduplicates files across tools. Every inline card for
      // the same file must select that same surviving panel entry.
      id: `${messageId}:file:${rel}`,
      messageId,
      lang: ext,
      kind: 'binary',
      body: '',
      title: abs.split('/').pop() || abs,
      relativePath: rel,
    });
  }
  return out;
}

/** Translate CLI-visible paths into a path accepted by the authenticated files API. */
export function workspaceRelativePath(
  filePath: string,
  username: string,
  siteOrigin?: string,
): string | null {
  if (!username) return null;
  let pathname = filePath.split(/[?#]/, 1)[0] ?? '';
  if (siteOrigin) {
    try {
      const url = new URL(filePath, siteOrigin);
      if (url.origin !== siteOrigin) return null;
      pathname = url.pathname;
    } catch {
      return null;
    }
  }
  let decoded: string;
  try {
    decoded = decodeURIComponent(pathname);
  } catch {
    decoded = pathname;
  }
  const prefix = ['/run/tenant-home/', `/workspace/${username}/`].find((p) =>
    decoded.startsWith(p),
  );
  if (!prefix) return null;
  const rel = decoded.slice(prefix.length);
  if (!rel || rel.includes('\\') || rel.includes('\0')) return null;
  if (rel.split('/').some((part) => !part || part === '.' || part === '..')) return null;
  return rel;
}

export function workspaceFileArtifact(relativePath: string): Artifact {
  const filename = relativePath.split('/').pop() || relativePath;
  return {
    id: `workspace-file:${relativePath}`,
    messageId: '',
    lang: filename.split('.').pop()?.toLowerCase() ?? '',
    kind: 'binary',
    body: '',
    title: displayWorkspaceFileName(relativePath),
    relativePath,
  };
}

const STORED_UPLOAD = /^uploads\/([0-9a-f]{8})(?:-[0-9a-f]{4}){3}-[0-9a-f]{12}_([^/]+)$/i;

/** Only direct uploads get a storage UUID; other filenames stay intact. */
export function displayWorkspaceFileName(relativePath: string): string {
  const filename = relativePath.split('/').pop() || relativePath;
  return STORED_UPLOAD.exec(relativePath)?.[2] ?? filename;
}

export function workspaceUploadId(relativePath: string): string | null {
  return STORED_UPLOAD.exec(relativePath)?.[1] ?? null;
}
import { AUDIO_MIME_TYPES } from '../../../server/src/audio-formats';
